import { ReactElement, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';

import api from 'config/api';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

type Props = {
  show: boolean;
  onClose?(): void;
};

type Status = 'idle' | 'sending' | 'sent' | 'error';

type Message = {
  name: string;
  email: string;
  message: string;
};

// This schema is used to validate the form inside TextMeModal.
const schema = yup.object().shape({
  name: yup.string().required('Please provide your name'),
  email: yup
    .string()
    .email("Ups! it's not a valid email address")
    .required('Please provide your email address'),
  message: yup.string().required('Please fill your message'),
});

function TextMeModal(props: Props): ReactElement<HTMLDivElement> | null {
  const [status, setStatus] = useState<Status>('idle');
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<Message>({
    resolver: yupResolver(schema),
  });

  // Call this callback when the user submit the form. The callback will send
  // the message via API.
  const onSubmit: SubmitHandler<Message> = async (form) => {
    setStatus('sending');

    let requestOptions = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(form),
    };

    let response = await fetch(api.message, requestOptions);

    if (response.ok) {
      setStatus('sent');
    } else {
      setStatus('error');
    }
  };

  if (!props.show) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black z-10 bg-opacity-50">
      <div className="mx-auto w-1/3 h-full flex flex-row items-center">
        <div className="bg-white rounded-lg relative">
          <button
            className="h-10 w-10 rounded-full absolute top-0 right-0 text-2xl text-primary"
            onClick={props.onClose}
          >
            &times;
          </button>
          {status === 'error' && (
            <p className="p-4 mx-10 mt-10 rounded-xl bg-red-100 text-xs text-primary">
              Ups! it looks like you haven't given a correct information. Please
              try again.
            </p>
          )}
          {status === 'sent' && (
            <p className="p-4 mx-10 mt-10 rounded-xl bg-green-100 text-xs text-green-600">
              Thank you! I willl read your message and replay it soon.
            </p>
          )}
          <form className="p-10 w-full" onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-5 last:mb-0">
              <label
                className="mb-1 inline-block text-secondary text-sm font-display"
                htmlFor="name"
              >
                Name
              </label>
              <input
                className="py-3 px-5 w-full bg-gray-200 rounded-lg text-sm focus:outline-none"
                type="text"
                id="name"
                {...register('name')}
              />
              {errors.name?.message && (
                <span className="text-xs text-primary font-body">
                  {errors.name.message}
                </span>
              )}
            </div>
            <div className="mb-5 last:mb-0">
              <label
                className="mb-1 inline-block text-secondary text-sm font-display"
                htmlFor="email"
              >
                E-mail
              </label>
              <input
                className="py-3 px-5 w-full bg-gray-200 rounded-lg text-sm focus:outline-none"
                type="email"
                id="email"
                {...register('email')}
              />
              {errors.email?.message && (
                <span className="text-xs text-primary font-body">
                  {errors.email.message}
                </span>
              )}
            </div>
            <div className="mb-5 last:mb-0">
              <label
                className="mb-1 inline-block text-secondary text-sm font-display"
                htmlFor="message"
              >
                Message
              </label>
              <textarea
                className="py-3 px-5 w-full rounded-xl bg-gray-200 text-sm resize-none focus:outline-none"
                id="message"
                cols={30}
                rows={5}
                {...register('message')}
              ></textarea>
              {errors.message?.message && (
                <span className="text-xs text-primary font-body">
                  {errors.message.message}
                </span>
              )}
            </div>
            <div>
              <button className="py-3 w-full bg-primary flex flex-row justify-center text-white font-display hover:bg-secondary">
                {status === 'sending' ? 'Sending...' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TextMeModal;
